// Up/down vote control shared by both posts and replies.
//
// Compact column on the left side of every post/reply card. The voter
// can click to set their vote (+1 / -1) or click again to clear (0).
// We optimistically update local state and roll back on error so the UI
// stays snappy even on slow connections.
import { useState } from "react";
import { ChevronUp, ChevronDown } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function VoteButtons({ targetType, targetId, score, myVote, onChange, ownPost }) {
  const [local, setLocal] = useState({ score, myVote });
  const [busy, setBusy] = useState(false);

  const submit = async (next) => {
    if (busy || ownPost) return;
    const prev = { ...local };
    const newVote = local.myVote === next ? 0 : next;
    const delta = newVote - local.myVote;
    setLocal({ score: local.score + delta, myVote: newVote });
    setBusy(true);
    try {
      const path = targetType === "post"
        ? `/forum/posts/${targetId}/vote`
        : `/forum/replies/${targetId}/vote`;
      const { data } = await api.post(path, { value: newVote });
      setLocal({ score: data.score, myVote: data.my_vote });
      onChange?.(data);
    } catch (err) {
      setLocal(prev);
      toast.error(err?.response?.data?.detail || "Couldn't save vote");
    } finally {
      setBusy(false);
    }
  };

  const up = local.myVote === 1;
  const down = local.myVote === -1;
  return (
    <div className="flex flex-col items-center select-none" data-testid={`vote-${targetType}-${targetId}`}>
      <button
        type="button"
        onClick={() => submit(1)}
        disabled={ownPost || busy}
        title={ownPost ? "Can't vote on your own post" : "Upvote"}
        className={
          "w-7 h-7 rounded-md flex items-center justify-center transition-colors " +
          (up
            ? "bg-emerald-100 text-emerald-700"
            : "text-zinc-400 hover:text-emerald-600 hover:bg-emerald-50") +
          (ownPost ? " opacity-40 cursor-not-allowed" : "")
        }
        data-testid={`vote-up-${targetId}`}
      >
        <ChevronUp size={18} />
      </button>
      <span
        className={
          "text-xs font-bold py-1 " +
          (local.score > 0 ? "text-emerald-700"
            : local.score < 0 ? "text-rose-700" : "text-zinc-500")
        }
        data-testid={`vote-score-${targetId}`}
      >
        {local.score}
      </span>
      <button
        type="button"
        onClick={() => submit(-1)}
        disabled={ownPost || busy}
        title={ownPost ? "Can't vote on your own post" : "Downvote"}
        className={
          "w-7 h-7 rounded-md flex items-center justify-center transition-colors " +
          (down
            ? "bg-rose-100 text-rose-700"
            : "text-zinc-400 hover:text-rose-600 hover:bg-rose-50") +
          (ownPost ? " opacity-40 cursor-not-allowed" : "")
        }
        data-testid={`vote-down-${targetId}`}
      >
        <ChevronDown size={18} />
      </button>
    </div>
  );
}
